import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { DirectionalIcon } from '../DirectionalIcon';
import { useThemedRTLStyles } from '../../hooks/useThemedRTLStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { colors, typography } from '../../theme';

interface Props {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  /** 'close' for the first checkout step, otherwise a back arrow. */
  backIcon?: 'back' | 'close';
  cartCount?: number;
  onCartPress?: () => void;
  right?: React.ReactNode;
}

/**
 * Header for the customer-facing storefront (catalog / product / cart /
 * checkout / success): theme surface (light or dark), clears the status bar
 * on native, mirrors in Arabic.
 */
export const StorefrontHeader: React.FC<Props> = ({ title, subtitle, onBack, backIcon = 'back', cartCount, onCartPress, right }) => {
  const s = useThemedRTLStyles(getStyles);
  const insets = useSafeAreaInsets();
  const { t } = useLocale();

  return (
    <View style={[s.wrap, { paddingTop: Platform.OS === 'web' ? 0 : insets.top }]}>
      <View style={s.row}>
        {onBack ? (
          <TouchableOpacity style={s.iconBtn} onPress={onBack} hitSlop={8} accessibilityRole="button">
            {backIcon === 'close'
              ? <Ionicons name="close" size={22} color={colors.text.primary as string} />
              : <DirectionalIcon name="arrow-back" size={22} color={colors.text.primary as string} />}
          </TouchableOpacity>
        ) : null}
        <View style={s.titleWrap}>
          <Text style={s.title} numberOfLines={1}>{title}</Text>
          {subtitle ? <Text style={s.subtitle} numberOfLines={1}>{subtitle}</Text> : null}
        </View>
        {right}
        {onCartPress ? (
          <TouchableOpacity style={s.iconBtn} onPress={onCartPress} hitSlop={8} accessibilityLabel={t('storefront.viewCart')}>
            <Ionicons name="bag-handle-outline" size={22} color={colors.text.primary as string} />
            {!!cartCount && cartCount > 0 && (
              <View style={s.badge}>
                <Text style={s.badgeText}>{cartCount > 99 ? '99+' : cartCount}</Text>
              </View>
            )}
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
};

const getStyles = () =>
  StyleSheet.create({
    wrap: {
      backgroundColor: colors.background.surface,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 12,
      minHeight: 56,
      paddingVertical: 8,
    },
    iconBtn: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background.secondary,
    },
    titleWrap: { flex: 1, minWidth: 0, paddingHorizontal: 4 },
    title: {
      fontSize: 18,
      fontFamily: typography.fontFamily.bold,
      color: colors.text.primary,
    },
    subtitle: {
      fontSize: 12,
      fontFamily: typography.fontFamily.primary,
      color: colors.text.secondary,
      marginTop: 1,
    },
    badge: {
      position: 'absolute',
      top: -3,
      right: -3,
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      paddingHorizontal: 4,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.primary,
      borderWidth: 2,
      borderColor: colors.background.surface,
    },
    badgeText: {
      fontSize: 10,
      lineHeight: 12,
      fontFamily: typography.fontFamily.bold,
      color: colors.text.onPrimary,
      textAlign: 'center',
    },
  });
